import type { SponsorCampaign } from "../campaignTypes";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Opens a print-ready report window so the partner can Save as PDF. */
export function openPartnerReportPdf(campaign: SponsorCampaign): void {
  const html = buildPartnerReportHtml(campaign);
  const win = window.open("", "_blank");
  if (!win) {
    window.alert("Allow pop-ups for this site to download the PDF report.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  const triggerPrint = () => {
    try {
      win.print();
    } catch {
      // Partner can use the browser print button if auto-print is blocked.
    }
  };
  if (win.document.readyState === "complete") {
    setTimeout(triggerPrint, 350);
  } else {
    win.addEventListener("load", () => setTimeout(triggerPrint, 350));
  }
}

export function buildPartnerReportHtml(campaign: SponsorCampaign): string {
  const accent = escapeHtml(campaign.accentColor || "#2F7A3E");
  const headline = escapeHtml(
    campaign.headlineLine2
      ? `${campaign.headline} ${campaign.headlineLine2}`
      : campaign.headline
  );

  const glance = campaign.glanceFields
    .map(
      (field) => `
      <div class="glance">
        <div class="label">${escapeHtml(field.label)}</div>
        <div class="value">${escapeHtml(field.value)}</div>
      </div>`
    )
    .join("");

  const activations = campaign.activations
    .map((activation) => {
      const metrics = activation.metrics
        .map(
          (m) =>
            `<div class="metric"><span>${escapeHtml(m.label)}</span><strong>${escapeHtml(m.value)}</strong></div>`
        )
        .join("");
      return `
      <section class="card">
        <div class="kicker">Taping ${activation.number}</div>
        <h3>${escapeHtml(activation.title)}</h3>
        <p class="muted">${escapeHtml(activation.date)} · ${escapeHtml(activation.location)}</p>
        <p>${escapeHtml(activation.description)}</p>
        <div class="metrics">${metrics}</div>
      </section>`;
    })
    .join("");

  const platforms = campaign.platformPerformance.rows
    .map(
      (row) => `
      <tr>
        <td>${escapeHtml(row.platform)}</td>
        <td>${escapeHtml(row.contentNote || "")}</td>
        <td class="num">${escapeHtml(row.impressions)}</td>
        <td class="num">${row.sharePercent}%</td>
      </tr>`
    )
    .join("");

  const kpis = campaign.kpiSection.groups
    .map((group) => {
      const metrics = group.metrics
        .map(
          (m) => `
          <div class="kpi">
            <div class="label">${escapeHtml(m.label)}</div>
            <div class="value">${escapeHtml(m.value)}</div>
            ${m.explanation ? `<p class="muted">${escapeHtml(m.explanation)}</p>` : ""}
          </div>`
        )
        .join("");
      return `
        <section class="card">
          <h3>${escapeHtml(group.heading)}</h3>
          <div class="kpi-grid">${metrics}</div>
        </section>`;
    })
    .join("");

  const media = campaign.gallery.images
    .map((img) => {
      const link = img.href
        ? `<a href="${escapeHtml(img.href)}">${escapeHtml(img.caption)}${img.badge ? ` (${escapeHtml(img.badge)})` : ""}</a>`
        : escapeHtml(img.caption);
      return `<li>${link}</li>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(campaign.campaignName)} · Partner report</title>
  <style>
    :root { --accent: ${accent}; --text: #111; --muted: #555; --line: #ddd; --bg: #fff; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: var(--text);
      background: var(--bg);
      font-family: Inter, Helvetica, Arial, sans-serif;
      font-size: 11.5pt;
      line-height: 1.45;
    }
    .page { max-width: 900px; margin: 0 auto; padding: 28px 32px 48px; }
    .brand { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 24px; }
    .brand img { height: 36px; width: auto; max-width: 180px; object-fit: contain; }
    .eyebrow { color: var(--accent); font-size: 10pt; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin: 0 0 8px; }
    h1 { font-size: 26pt; line-height: 1.15; margin: 0 0 10px; letter-spacing: -0.02em; }
    h2 { font-size: 16pt; margin: 28px 0 10px; }
    h3 { font-size: 13pt; margin: 0 0 6px; }
    p { margin: 0 0 10px; }
    .muted { color: var(--muted); font-size: 10pt; }
    .hero-metric { display: flex; gap: 24px; flex-wrap: wrap; margin: 18px 0 8px; padding: 14px 16px; border: 1px solid var(--line); border-radius: 12px; }
    .hero-metric .value { font-size: 22pt; font-weight: 700; color: var(--accent); }
    .glance-grid, .metrics, .kpi-grid { display: grid; gap: 10px; }
    .glance-grid { grid-template-columns: repeat(3, 1fr); margin: 16px 0 8px; }
    .glance, .kpi, .card { border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; }
    .label { font-size: 8.5pt; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-bottom: 4px; }
    .value { font-weight: 700; }
    .kicker { color: var(--accent); font-size: 9pt; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 4px; }
    .metrics { grid-template-columns: repeat(3, 1fr); margin-top: 10px; }
    .metric { display: flex; flex-direction: column; gap: 2px; }
    .metric span { font-size: 9pt; color: var(--muted); }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { text-align: left; padding: 8px 6px; border-bottom: 1px solid var(--line); font-size: 10pt; vertical-align: top; }
    th { color: var(--muted); font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.06em; }
    td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .kpi-grid { grid-template-columns: repeat(2, 1fr); margin-top: 8px; }
    .stack { display: grid; gap: 12px; }
    ul { padding-left: 18px; margin: 8px 0; }
    li { margin: 4px 0; }
    a { color: var(--accent); }
    .closing { margin-top: 28px; padding: 16px; border: 1px solid var(--line); border-radius: 12px; }
    .print-note { display: none; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .page { max-width: none; padding: 0; }
      .card, .glance, .kpi, .hero-metric, .closing { break-inside: avoid; }
      a { text-decoration: none; }
    }
    @media screen {
      .print-note {
        display: block;
        margin: 0 0 18px;
        padding: 10px 12px;
        border-radius: 10px;
        background: #f4f6f5;
        border: 1px solid var(--line);
        font-size: 10pt;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <p class="print-note">In the print dialog, choose <strong>Save as PDF</strong> as the destination.</p>
    <div class="brand">
      ${campaign.sponsorLogo ? `<img src="${escapeHtml(campaign.sponsorLogo)}" alt="${escapeHtml(campaign.sponsorName)}" />` : `<strong>${escapeHtml(campaign.sponsorName)}</strong>`}
      <img src="${escapeHtml(campaign.comedyuoLogo)}" alt="ComedyUO" />
    </div>
    <p class="eyebrow">${escapeHtml(campaign.eyebrow)}</p>
    <h1>${headline}</h1>
    <p class="muted">${escapeHtml(campaign.venue)} · ${escapeHtml(campaign.dateRange)} · Reporting ${escapeHtml(campaign.reportingPeriod)}</p>
    <p>${escapeHtml(campaign.campaignSummary)}</p>
    <div class="hero-metric">
      <div>
        <div class="label">${escapeHtml(campaign.featuredMetric.label)}</div>
        <div class="value">${escapeHtml(campaign.featuredMetric.value)}</div>
      </div>
      <div>
        <div class="label">Detail</div>
        <p class="muted" style="margin:0">${escapeHtml(campaign.featuredMetric.detail)}</p>
      </div>
    </div>

    <h2>At a glance</h2>
    <div class="glance-grid">${glance}</div>

    <h2>${escapeHtml(campaign.activationsSection.title)}</h2>
    <p class="muted">${escapeHtml(campaign.activationsSection.description)}</p>
    <div class="stack">${activations}</div>

    <h2>${escapeHtml(campaign.platformPerformance.title)}</h2>
    <p class="muted">${escapeHtml(campaign.platformPerformance.description)}</p>
    <table>
      <thead>
        <tr>
          <th>Platform</th>
          <th>Notes</th>
          <th>${escapeHtml(campaign.platformPerformance.metricColumnLabel || "Views")}</th>
          <th>Share</th>
        </tr>
      </thead>
      <tbody>${platforms}</tbody>
    </table>
    <p><strong>${escapeHtml(campaign.platformPerformance.totalImpressionsLabel)}:</strong> ${escapeHtml(campaign.platformPerformance.totalImpressionsValue)}</p>

    <h2>${escapeHtml(campaign.kpiSection.title)}</h2>
    <p class="muted">${escapeHtml(campaign.kpiSection.description)}</p>
    <div class="stack">${kpis}</div>

    <h2>${escapeHtml(campaign.gallery.title)}</h2>
    ${campaign.gallery.description ? `<p class="muted">${escapeHtml(campaign.gallery.description)}</p>` : ""}
    <ul>${media}</ul>

    <div class="closing">
      <h2 style="margin-top:0">${escapeHtml(campaign.closing.thankYouHeadline)}</h2>
      <p>${escapeHtml(campaign.closing.summary)}</p>
      <p><strong>${escapeHtml(campaign.closing.highlightCallout)}</strong></p>
      ${campaign.closing.nextStep ? `<p class="muted">${escapeHtml(campaign.closing.nextStep)}</p>` : ""}
      <p class="muted">${escapeHtml(campaign.closing.contact.name)} · ${escapeHtml(campaign.closing.contact.role)} · ${escapeHtml(campaign.closing.contact.email)}</p>
    </div>
  </div>
</body>
</html>`;
}
